import type { CollectedMeeting, MeetingGuest } from '../../domain/meeting';
import { Calendar } from '../../domain/meeting';
import type { CollectedDataItem } from '../../domain/message';

import SharedDataExtractionService from './sharedDataExtractionService';

const createMeetingDataExtractionService = () => {
  const { isNotIgnoredDomain, uniqueAndSorted } = SharedDataExtractionService;
  const extractDomainFromEmail = (email: string) => {
    const [, domain] = email.split('@');
    return domain;
  };

  const extractEmailFromString = (input: string) => {
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;

    const matches = input.match(emailRegex);
    if (matches && matches.length > 0) {
      return matches[0];
    }
  };

  const extractMeetingGuestsFromAttributes = (collectedData: CollectedDataItem[]): MeetingGuest[] => {
    const collectedInfoFromAttributes = collectedData.find((item) => item.name === 'Search meeting emails');
    const collectedEmailsFromAttributes: string[] = collectedInfoFromAttributes?.attributes?.['data-email'] || [];

    const collectedNameInfoFromAttributes = collectedData.find((item) => item.name === 'Search meeting names');
    const collectedNamesFromAttributes = collectedNameInfoFromAttributes?.content || [];

    return collectedEmailsFromAttributes.map((email: string, index: number) => ({
      email,
      name: collectedNamesFromAttributes[index] || '',
    }));
  };

  const extractMeetingGuestsFromLinks = (collectedData: CollectedDataItem[]): MeetingGuest[] => {
    const collectedInfoFromLinks = collectedData.find((item) => item.name === 'Search meeting emails from links');
    const collectedEmailsFromLinks: string[] = collectedInfoFromLinks?.content || [];

    return collectedEmailsFromLinks.map((email: string) => ({ email, name: '' }));
  };

  const extractMeetingGuestsFromTexts = (collectedData: CollectedDataItem[]): MeetingGuest[] => {
    const collectedInfoFromText = collectedData.find((item) => item.name === 'Search meeting emails from data-text');
    const collectedEmailsStringsFromTexts = collectedInfoFromText?.attributes?.['data-text'];
    const collectedEmailsFromTexts = Array.isArray(collectedEmailsStringsFromTexts)
      ? (collectedEmailsStringsFromTexts.map(extractEmailFromString).filter(Boolean) as string[])
      : [];

    return collectedEmailsFromTexts.map((email: string) => ({ email, name: '' }));
  };

  const extractMeetingGuestsForGoogle = (collectedData: CollectedDataItem[]): MeetingGuest[] => {
    const guestsFromAttributes = extractMeetingGuestsFromAttributes(collectedData);
    const guestsFromLinks = extractMeetingGuestsFromLinks(collectedData);
    const guestsFromTexts = extractMeetingGuestsFromTexts(collectedData);

    return [...guestsFromAttributes, ...guestsFromLinks, ...guestsFromTexts];
  };

  const extractMeetingGuestsForOutlook = (collectedData: CollectedDataItem[]): MeetingGuest[] => {
    const collectedInfo = collectedData.find((item) => item.name === 'Search meeting emails');
    const collectedEmailsFromContent = collectedInfo?.content || [];

    const collectedInfoFromDetails = collectedData.find(
      (item) => item.name === 'Search meeting emails from event details'
    );
    const collectedEmailsFromDetails = collectedInfoFromDetails?.attributes?.['aria-label'] || [];

    return [...collectedEmailsFromContent, ...collectedEmailsFromDetails].map((email: string) => ({ email, name: '' }));
  };

  const extractMeetingGuests = (collectedData: CollectedDataItem[], calendar: Calendar): MeetingGuest[] => {
    if (calendar === Calendar.google) {
      return extractMeetingGuestsForGoogle(collectedData);
    } else if (calendar === Calendar.outlook) {
      return extractMeetingGuestsForOutlook(collectedData);
    }
    return [];
  };

  const extractMeetingDomains = (collectedEmails: MeetingGuest[]): string[] => {
    const domains = collectedEmails
      .map((guest) => guest.email)
      .filter((email) => email.includes('@'))
      .map(extractDomainFromEmail)
      .map((domain) => domain.toLowerCase())
      .filter(isNotIgnoredDomain);
    return uniqueAndSorted(domains);
  };

  const extractMeetingInfo = (collectedData: CollectedDataItem[]): { title: string; time: string; isNew: boolean } => {
    const collectedTitle = collectedData.find(
      (item) => item.name === 'Search meeting title' || item.name === 'Search meeting title from event details'
    )?.content;
    const collectedTime = collectedData.find(
      (item) => item.name === 'Search meeting time' || item.name === 'Search meeting time from event details'
    )?.content;

    return collectedTitle && typeof collectedTitle === 'string'
      ? { title: collectedTitle, time: (collectedTime as string) || '', isNew: false }
      : { title: 'New meeting', time: '', isNew: true };
  };

  const extractMeetingBriefUrls = (domains: string[], guests: MeetingGuest[]): Record<string, string> => {
    const meetingBriefUrls: Record<string, string> = {};
    domains.forEach((domain) => {
      let meetingBriefUrl = `https://${location.host}/#/sales/ai-meeting-agent/chat/latest/${encodeURIComponent(
        domain
      )}`;
      const domainGuests = guests.filter((g) => extractDomainFromEmail(g.email) === domain).map((g) => g.name);
      if (domainGuests.length > 0) {
        meetingBriefUrl += `?attendee=${encodeURIComponent(domainGuests.join(','))}`;
      }
      meetingBriefUrls[domain] = meetingBriefUrl;
    });
    return meetingBriefUrls;
  };

  const extractMeeting = (collectedData: CollectedDataItem[], calendar: Calendar): CollectedMeeting => {
    const guests = extractMeetingGuests(collectedData, calendar);
    const calendarOwnerDomain = extractMeetingGuestsFromTexts(collectedData)
      .filter((guest) => guest.email.includes('@'))
      .map((guest) => extractDomainFromEmail(guest.email))[0];
    const domains = extractMeetingDomains(guests);

    const meetingInfo = extractMeetingInfo(collectedData);
    const meetingBriefUrls = extractMeetingBriefUrls(domains, guests);

    return { ...meetingInfo, guests, domains, calendarOwnerDomain, meetingBriefUrls };
  };

  const isTheSameMeetingWithLessEmails = (
    meeting: CollectedMeeting | undefined,
    prevCollectedMeeting: CollectedMeeting | undefined
  ): boolean => {
    if (!prevCollectedMeeting && !meeting) {
      return true;
    }
    if ((!prevCollectedMeeting && meeting) || (!meeting && prevCollectedMeeting)) {
      return false;
    }
    return (
      !!meeting &&
      !!prevCollectedMeeting &&
      meeting.time === prevCollectedMeeting.time &&
      meeting.title === prevCollectedMeeting.title &&
      meeting.guests?.length < prevCollectedMeeting.guests?.length
    );
  };

  return {
    extractMeeting,
    extractDomainFromEmail,
    isTheSameMeetingWithLessEmails,
    extractEmailFromString,
  };
};

const MeetingDataExtractionService = createMeetingDataExtractionService();

export default MeetingDataExtractionService;
